import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, Tooltip, Alert
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import Swal from 'sweetalert2';

function GestionBancos() {
    const server = process.env.REACT_APP_API_ROUTE;
    const { user } = useUser();
    const { tienePermiso } = useAccesos();

    const [bancos, setBancos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [modoEdicion, setModoEdicion] = useState(false);
    const [bancoActual, setBancoActual] = useState({
        idBanco: 0,
        nombreBanco: ''
    });

    useEffect(() => {
        cargarBancos();
    }, []);

    const cargarBancos = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${server}/ChkBancoes`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setBancos(data.$values || data);
            } else {
                Swal.fire('Error', 'No se pudieron cargar los bancos', 'error');
            }
        } catch (error) {
            console.error('Error al cargar bancos:', error);
            Swal.fire('Error', 'Error al cargar bancos', 'error');
        } finally {
            setLoading(false);
        }
    };

    const abrirDialogoNuevo = () => {
        setBancoActual({
            idBanco: 0,
            nombreBanco: ''
        });
        setModoEdicion(false);
        setOpenDialog(true);
    };

    const abrirDialogoEditar = (banco) => {
        setBancoActual({
            idBanco: banco.idBanco,
            nombreBanco: banco.nombreBanco
        });
        setModoEdicion(true);
        setOpenDialog(true);
    };

    const cerrarDialogo = () => {
        setOpenDialog(false);
        setBancoActual({ idBanco: 0, nombreBanco: '' });
    };

    const guardarBanco = async () => {
        if (!bancoActual.nombreBanco.trim()) {
            Swal.fire('Validación', 'El nombre del banco es obligatorio', 'warning');
            return;
        }

        setLoading(true);
        try {
            const usuarioPadded = (user.codigoUsuario || '').padEnd(20, ' ');
            
            const bancoData = {
                ...bancoActual,
                modificacion: modoEdicion ? 2 : 1,
                ingresadoPor: modoEdicion ? bancoActual.ingresadoPor : usuarioPadded,
                modificadoPor: modoEdicion ? usuarioPadded : null,
                fechaIngreso: modoEdicion ? bancoActual.fechaIngreso : new Date().toISOString(),
                fechaModificacion: modoEdicion ? new Date().toISOString() : null
            };

            const url = modoEdicion 
                ? `${server}/ChkBancoes/${bancoActual.idBanco}`
                : `${server}/ChkBancoes`;

            const response = await fetch(url, {
                method: modoEdicion ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(bancoData)
            });

            if (response.ok) {
                Swal.fire({
                    icon: 'success',
                    title: modoEdicion ? 'Banco actualizado' : 'Banco creado',
                    timer: 1500,
                    showConfirmButton: false
                });
                cerrarDialogo();
                cargarBancos();
            } else {
                const errorData = await response.json().catch(() => ({}));
                Swal.fire('Error', errorData.message || 'Error al guardar el banco', 'error');
            }
        } catch (error) {
            console.error('Error al guardar banco:', error);
            Swal.fire('Error', 'Error al guardar el banco', 'error');
        } finally {
            setLoading(false);
        }
    };

    const eliminarBanco = async (id) => {
        const resultado = await Swal.fire({
            title: '¿Está seguro?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33'
        });

        if (resultado.isConfirmed) {
            setLoading(true);
            try {
                const response = await fetch(`${server}/ChkBancoes/${id}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (response.ok) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Banco eliminado',
                        timer: 1500,
                        showConfirmButton: false
                    });
                    cargarBancos();
                } else {
                    const errorData = await response.json().catch(() => ({}));
                    Swal.fire('Error', errorData.message || 'No se puede eliminar el banco', 'error');
                }
            } catch (error) {
                console.error('Error al eliminar banco:', error);
                Swal.fire('Error', 'Error al eliminar el banco', 'error');
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className="right_col" role="main">
            <div className="page-title">
                <div className="title_left">
                    <h3>Gestión de Bancos</h3>
                </div>
            </div>

            <div className="clearfix"></div>

            <div className="row">
                <div className="col-md-12">
                    <div className="x_panel">
                        <div className="x_title">
                            <h2>Lista de Bancos</h2>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Add />}
                                onClick={abrirDialogoNuevo}
                                style={{ float: 'right' }}
                                disabled={!tienePermiso('Editar Banco')}
                            >
                                Nuevo Banco
                            </Button>
                            <div className="clearfix"></div>
                        </div>
                        <div className="x_content">
                            {loading && <Alert severity="info">Cargando...</Alert>}
                            
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Nombre del Banco</TableCell>
                                            <TableCell>Ingresado Por</TableCell>
                                            <TableCell>Fecha Ingreso</TableCell>
                                            <TableCell align="center">Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {bancos.filter(b => b.modificacion !== 3).map((banco) => (
                                            <TableRow key={banco.idBanco}>
                                                <TableCell>{banco.idBanco}</TableCell>
                                                <TableCell>{banco.nombreBanco}</TableCell>
                                                <TableCell>{banco.ingresadoPor?.trim()}</TableCell>
                                                <TableCell>
                                                    {banco.fechaIngreso 
                                                        ? new Date(banco.fechaIngreso).toLocaleDateString() 
                                                        : ''}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="Editar">
                                                        <IconButton
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => abrirDialogoEditar(banco)}
                                                            disabled={!tienePermiso('Editar Banco')}
                                                        >
                                                            <Edit />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Eliminar">
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            onClick={() => eliminarBanco(banco.idBanco)}
                                                            disabled={!tienePermiso('Eliminar Banco')}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    </div>
                </div>
            </div>

            {/* Dialog para crear/editar banco */}
            <Dialog open={openDialog} onClose={cerrarDialogo} maxWidth="sm" fullWidth>
                <DialogTitle>{modoEdicion ? 'Editar Banco' : 'Nuevo Banco'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            label="Nombre del Banco *"
                            fullWidth
                            value={bancoActual.nombreBanco}
                            onChange={(e) => setBancoActual({ ...bancoActual, nombreBanco: e.target.value })}
                            margin="normal"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cerrarDialogo}>Cancelar</Button>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={guardarBanco}
                        disabled={loading || !tienePermiso('Editar Banco')}
                    >
                        {loading ? 'Guardando...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default GestionBancos;
